import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      try {
        const userData = await authAPI.getCurrentUser();
        if (userData.is_admin) {
          setUser(userData);
        } else {
          // Not an admin, log out
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_refresh_token');
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, refresh_token } = response;

      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_refresh_token', refresh_token);

      // Fetch user data and verify admin status
      const userData = await authAPI.getCurrentUser();

      if (!userData.is_admin) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        throw new Error('Access denied. Admin privileges required.');
      }

      setUser(userData);
      navigate('/');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
